import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Warning, X, Trash } from '@phosphor-icons/react';

/**
 * DeleteConfirmModal — reusable confirmation gate for destructive actions.
 *
 * Two-step protection:
 *   1. User must check "I understand this action"
 *   2. User must type the confirmation phrase (default: "DELETE")
 *
 * Props:
 *   open:           boolean
 *   onClose:        () => void
 *   onConfirm:      ({ reason, hardDelete }) => Promise<void>
 *   targetType:     "user" | "shop" | string — used in copy
 *   targetName:     human-readable identifier (email, shop name)
 *   warning:        explanation text shown to admin
 *   allowHardDelete: show "Hard delete" toggle (server still gates by env)
 *   confirmPhrase:  word user must type (default "DELETE")
 */
export function DeleteConfirmModal({
  open,
  onClose,
  onConfirm,
  targetType = 'user',
  targetName = '',
  warning,
  allowHardDelete = false,
  confirmPhrase = 'DELETE',
}) {
  const [phrase, setPhrase] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [hardDelete, setHardDelete] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPhrase('');
      setAcknowledged(false);
      setHardDelete(false);
      setReason('');
      setSubmitting(false);
    }
  }, [open]);

  const phraseOk = phrase.trim().toUpperCase() === confirmPhrase.toUpperCase();
  const canConfirm = phraseOk && acknowledged && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm({ reason: reason.trim() || null, hardDelete });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[210] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="delete-confirm-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0c0c14] border border-[#EF4444]/30 rounded-2xl p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#EF4444]/15 flex items-center justify-center">
                  <Warning size={18} weight="fill" className="text-[#EF4444]" />
                </div>
                <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
                  Delete {targetType}?
                </h3>
              </div>
              <button onClick={onClose} className="text-[#666] hover:text-white" data-testid="modal-close">
                <X size={18} />
              </button>
            </div>

            {targetName && (
              <p className="text-[13px] text-[#9D9D9D] mb-4">
                Target: <span className="font-mono text-white">{targetName}</span>
              </p>
            )}

            {warning && (
              <div className="bg-[#F59E0B]/8 border border-[#F59E0B]/20 rounded-lg p-3 mb-4">
                <p className="text-[12px] text-[#F59E0B] leading-relaxed">{warning}</p>
              </div>
            )}

            <div className="space-y-3 mb-5">
              <label className="block">
                <span className="text-[11px] text-[#888] uppercase tracking-wider font-semibold">
                  Reason (optional)
                </span>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., spam, ToS violation, owner request..."
                  className="w-full mt-1 bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white placeholder:text-[#444] focus:outline-none focus:border-[#EF4444]/40"
                  data-testid="delete-reason"
                />
              </label>

              <label className="block">
                <span className="text-[11px] text-[#888] uppercase tracking-wider font-semibold">
                  Type <span className="font-mono text-[#EF4444]">{confirmPhrase}</span> to confirm
                </span>
                <input
                  type="text"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  className="w-full mt-1 bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] font-mono text-white focus:outline-none focus:border-[#EF4444]/40"
                  data-testid="delete-phrase-input"
                  autoComplete="off"
                />
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-0.5"
                  data-testid="delete-acknowledge"
                />
                <span className="text-[12px] text-[#9D9D9D]">
                  I understand this will deactivate the {targetType} and remove their access. Historical data is preserved.
                </span>
              </label>

              {allowHardDelete && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hardDelete}
                    onChange={(e) => setHardDelete(e.target.checked)}
                    className="mt-0.5"
                    data-testid="delete-hard-toggle"
                  />
                  <span className="text-[12px] text-[#EF4444]">
                    Hard delete (permanent — cannot be restored)
                  </span>
                </label>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-white/[0.04] text-[#9D9D9D] text-[13px] font-medium hover:bg-white/[0.08]"
                data-testid="delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="px-4 py-2 rounded-lg bg-[#EF4444] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#DC2626]"
                data-testid="delete-confirm"
              >
                <Trash size={14} weight="bold" />
                {submitting ? 'Deleting...' : hardDelete ? 'Hard Delete' : 'Delete'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default DeleteConfirmModal;
